import {useEffect, useState} from "react";
import {App as AntdApp, Select, Table, Tag} from "antd";
import {useForm, Controller} from "react-hook-form";
import {ModalForm} from "~/components";
import {InputField, NumberField, SelectField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetKeywordItemsQuery} from "~/reduxs/api/keywordApiSlice";
import {
	usePreviewContentPlanMutation,
	useCreateContentPlanMutation,
	useGetContentTargetsQuery,
} from "~/reduxs/api/contentApiSlice";

const GOAL_OPTIONS = [
	{value: 'traffic', label: 'Tăng lưu lượng tự nhiên (traffic)'},
	{value: 'branding', label: 'Xây thương hiệu / phủ chủ đề'},
	{value: 'conversion', label: 'Tăng chuyển đổi / bán hàng'},
];

const REVIEW_OPTIONS = [
	{value: '', label: 'Theo cài đặt dự án'},
	{value: 'review_all', label: 'Duyệt dàn ý + duyệt bài'},
	{value: 'review_article', label: 'Chỉ duyệt bài'},
	{value: 'full_auto', label: 'Tự động hoàn toàn'},
];

const TYPE_LABELS = {guide: 'Hướng dẫn', compare: 'So sánh', list: 'Danh sách', news: 'Tin tức'};

/**
 * Wizard tạo kế hoạch bài viết (Đợt 2) — 2 bước trong 1 modal:
 *  1. Chọn bộ từ khoá (từ Kho từ khoá của dự án + nhập tự do) + mục tiêu + nhịp đăng
 *     → "AI đề xuất": gom cụm chủ đề thành danh sách bài (previewContentPlan).
 *  2. Duyệt danh sách bài AI đề xuất (chọn/bỏ từng bài) → tạo kế hoạch: bài được trải
 *     lịch theo N bài/tuần, trừ quota theo số bài đã chọn.
 */
function ContentPlanWizard({projectId, open, onCancel, onCreated}) {

	const {message} = AntdApp.useApp();
	const [step, setStep] = useState(1);
	const [articles, setArticles] = useState([]);
	const [selected, setSelected] = useState([]);

	const {data: vault} = useGetKeywordItemsQuery(projectId, {skip: !open});
	const {data: targetData} = useGetContentTargetsQuery(projectId, {skip: !open});
	const [preview, {isLoading: previewing}] = usePreviewContentPlanMutation();
	const [create, {isLoading: creating}] = useCreateContentPlanMutation();

	const {control, handleSubmit, getValues, watch, reset} = useForm({
		defaultValues: {name: '', keywords: [], goal: 'traffic', per_week: 3, count: 0, review_mode: '', publish_target_id: 0},
	});

	useEffect(() => {
		if (open) {
			reset({name: '', keywords: [], goal: 'traffic', per_week: 3, count: 0, review_mode: '', publish_target_id: 0});
			setStep(1);
			setArticles([]);
			setSelected([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Đợt 3: kênh tự đăng — chỉ có nghĩa với kế hoạch tự động hoàn toàn.
	const watchReview = watch('review_mode');
	const activeTargets = (targetData?.targets || []).filter((t) => t.status === 'active');

	// Options từ Kho từ khoá (kèm volume nếu có) + cho phép nhập từ khoá tự do (mode tags).
	const vaultItems = vault?.items || [];
	const keywordOptions = vaultItems.map((it) => ({
		value: it.keyword,
		label: it.volume != null ? `${it.keyword} (${Number(it.volume).toLocaleString('vi-VN')})` : it.keyword,
	}));

	const runPreview = async (values) => {
		if (!values.keywords?.length) {
			message.warning('Chọn hoặc nhập ít nhất 1 từ khoá.');
			return;
		}
		try {
			// Gắn lại volume/position từ kho cho từ khoá được chọn (AI ưu tiên theo số liệu).
			const enriched = values.keywords.map((kw) => {
				const hit = vaultItems.find((it) => it.keyword === kw);
				return {keyword: kw, volume: hit?.volume ?? null, position: hit?.position ?? null};
			});
			const res = await preview({projectId, keywords: enriched, goal: values.goal, count: values.count || 0}).unwrap();
			const list = res?.articles || [];
			if (!list.length) {
				message.warning('AI chưa đề xuất được bài nào — thử đổi bộ từ khoá.');
				return;
			}
			setArticles(list);
			setSelected(list.map((_, i) => i));
			setStep(2);
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const submitPlan = async () => {
		const values = getValues();
		const chosen = articles.filter((_, i) => selected.includes(i));
		if (!chosen.length) {
			message.warning('Chọn ít nhất 1 bài để tạo kế hoạch.');
			return;
		}
		try {
			await create({
				projectId,
				name: values.name,
				goal: values.goal,
				review_mode: values.review_mode,
				per_week: values.per_week || 3,
				publish_target_id: values.review_mode === 'full_auto' ? (values.publish_target_id || 0) : 0,
				articles: chosen,
			}).unwrap();
			message.success(`Đã tạo kế hoạch ${chosen.length} bài — AI xử lý dần theo lịch`);
			onCreated?.();
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const columns = [
		{
			title: 'Bài viết đề xuất',
			dataIndex: 'title',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>{r.title || r.keyword}</div>
					<div style={{fontSize: 12.5, color: '#6b7280'}}>
						Từ khoá: <b>{r.keyword}</b>
						{r.sub_keywords?.length > 0 && <> · phụ: {r.sub_keywords.join(', ')}</>}
					</div>
					{r.reason && <div style={{fontSize: 12.5, color: '#0891b2'}}>{r.reason}</div>}
				</div>
			),
		},
		{
			title: 'Loại',
			dataIndex: 'article_type',
			width: 110,
			render: (v) => TYPE_LABELS[v] || v,
		},
		{
			title: 'Ưu tiên',
			dataIndex: 'priority',
			width: 90,
			render: (v) => <Tag color={v === 1 ? 'red' : v === 2 ? 'orange' : 'default'}>P{v}</Tag>,
		},
	];

	return (
		<ModalForm
			open={open}
			icon="fa-light fa-calendar-lines-pen"
			title={step === 1 ? 'Tạo kế hoạch bài viết' : `Duyệt kế hoạch — AI đề xuất ${articles.length} bài`}
			subtitle={step === 1
				? 'AI gom cụm bộ từ khoá thành chủ đề và đề xuất danh sách bài để bạn duyệt'
				: 'Bỏ chọn bài không muốn viết; bài được trải lịch theo nhịp đăng đã chọn'}
			onCancel={step === 1 ? onCancel : () => setStep(1)}
			cancelText={step === 1 ? 'Huỷ' : 'Quay lại'}
			onOk={step === 1 ? handleSubmit(runPreview) : submitPlan}
			okText={step === 1 ? 'AI đề xuất bài viết' : `Tạo kế hoạch (${selected.length} bài)`}
			loading={previewing || creating}
			width={760}
		>
			{step === 1 && (
				<>
					<Controller control={control} name="name" render={({field}) => (
						<InputField label="Tên kế hoạch" placeholder={`vd: Content tháng ${new Date().getMonth() + 1}`} {...field} />
					)} />
					<Controller control={control} name="keywords" render={({field}) => (
						<div className="form-group" style={{marginBottom: 14}}>
							<label style={{display: 'block', marginBottom: 6, fontWeight: 500}}>
								Bộ từ khoá ({field.value?.length || 0} đã chọn — từ Kho từ khoá hoặc nhập tự do)
							</label>
							<Select
								mode="tags"
								style={{width: '100%'}}
								placeholder="Chọn từ Kho từ khoá hoặc gõ từ khoá mới rồi Enter..."
								options={keywordOptions}
								value={field.value}
								onChange={field.onChange}
								maxTagCount={8}
							/>
						</div>
					)} />
					<Controller control={control} name="goal" render={({field}) => (
						<SelectField label="Mục tiêu nội dung" options={GOAL_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
					<Controller control={control} name="per_week" render={({field}) => (
						<NumberField label="Nhịp đăng (bài / tuần)" min={1} max={7} {...field} />
					)} />
					<Controller control={control} name="count" render={({field}) => (
						<NumberField label="Số bài mong muốn (0 = AI tự quyết theo cụm chủ đề)" min={0} max={30} {...field} />
					)} />
					<Controller control={control} name="review_mode" render={({field}) => (
						<SelectField label="Chế độ duyệt của kế hoạch" options={REVIEW_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
					{watchReview === 'full_auto' && activeTargets.length > 0 && (
						<Controller control={control} name="publish_target_id" render={({field}) => (
							<SelectField
								label="Tự đăng lên kênh (sau khi bài qua checklist — theo chế độ mặc định của kênh)"
								options={[
									{value: 0, label: 'Không tự đăng (bài dừng ở Hoàn tất)'},
									...activeTargets.map((t) => ({value: t.id, label: `${t.name} (${t.type})`})),
								]}
								style={{width: '100%'}}
								{...field}
							/>
						)} />
					)}
				</>
			)}

			{step === 2 && (
				<div className="table-scroll">
					<Table
						rowKey={(_, i) => i}
						dataSource={articles}
						columns={columns}
						pagination={false}
						size="small"
						scroll={{y: 380}}
						rowSelection={{
							selectedRowKeys: selected,
							onChange: (keys) => setSelected(keys),
						}}
					/>
				</div>
			)}
		</ModalForm>
	);
}

export default ContentPlanWizard;
